import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth-store";
import { useAppHelpers } from "@/composables/useAppHelpers";

import NotFound from "@/views/NotFound.vue";

import Landing from "../views/Landing.vue";

import Login from "../views/Auth/Login.vue";
import Register from "../views/Auth/Register.vue";
import ForgotPassword from "../views/Auth/ForgotPassword.vue";
import ResetPassword from "../views/Auth/ResetPassword.vue";
import Verify from "../views/Auth/Verify.vue";
import Logout from "../views/Auth/Logout.vue";

import Profile from "../views/Member/Profile.vue";
import Settings from "../views/Member/Settings.vue";

import Admin from "../views/Admin/Index.vue";
import Legal from "@/views/Legal/Legal.vue";
import LegalAlt from "@/views/Legal/LegalAlt.vue";

import SurveyResponse from "../views/Member/SurveyResponse.vue";

import BasicPage from "@/views/BasicPages/Index.vue";
import SpecificPage from "@/views/SpecificPages/Index.vue";

import Terms from "@/views/Legal/Terms.vue";
import FirstSetup from "@/views/Auth/FirstSetup.vue";
import RevokeSession from "@/views/Auth/RevokeSession.vue";
import Survey from "@/views/Survey/Survey.vue";

const authRoutes = [
    {
        path: "/login",
        name: "login",
        component: Login,
        meta: {
            type: ["auth_layout"],
        },
    },
    {
        path: "/register",
        name: "register",
        component: Register,
        meta: {
            type: ["auth_layout"],
        },
    },
    {
        path: "/forgot-password",
        name: "forgot-password",
        component: ForgotPassword,
        meta: {
            type: ["auth_layout"],
        },
    },
    {
        path: "/reset-password/:token",
        name: "reset-password",
        component: ResetPassword,
        props: (route) => ({
            token: route.params.token,
            email: route.query.email
        }),
        meta: {
            type: ["auth_layout"],
        },
    },
    {
        path: "/logout",
        name: "logout",
        component: Logout,
        meta: {
            type: ["all_auth"],
        },
    }
];
const userProfileRoutes = [
    {
        path: "/profile",
        name: "profile",
        component: Profile,
        meta: {
            type: ["all_auth"],
        },
    },
    {
        path: "/settings",
        name: "settings",
        component: Settings,
        meta: {
            type: ["all_auth"],
        },
    },
    {
        path: "/setup_account",
        name: "setup_account",
        component: FirstSetup,
        meta: {
            type: ["all_auth"],
            noHeader: true,
        },
    },
    {
        path: "/user/remove-session/revoke/:uid/:session_id",
        name: "revoke_session",
        component: RevokeSession,
        props: (route) => ({
            uid: route.params.uid,
            session_id: route.params.session_id,
        }),
    },
];
const adminRoutes = [
    {
        path: "/admin",
        name: "admin",
        component: Admin,
        meta: {
            type: ["admin", "moderator"],
        },
    }
];
const legalRoutes = [
    {
        path: "/legal/:slug",
        name: "legal",
        component: Legal,
        props: (route) => ({
            slug: route.params.slug,
        }),
    },
    {
        path: "/legal/v2/:slug",
        name: "legal_alt",
        component: LegalAlt,
        props: (route) => ({
            slug: route.params.slug,
        }),
    },
    
    {
        path: "/terms",
        name: "terms",
        component: Terms,
        meta: {
            noHeader: true,
        },
    }
];
const surveyRoutes = [
    {
        path: "/survey/view/:id",
        name: "single_survey",
        component: SurveyResponse,
        props: (route) => ({
            id: route.params.id,
        })
    },
    {
        path: "/survey/resume/:id/:resume",
        name: "edit-survey-page",
        component: SurveyResponse,
        props: (route) => ({
            edit_id: route.params.id,
            resume: route.params.resume,
        })
    },
    {
        path: "/survey/:type",
        name: "SurveyConsent",
        component: Survey,
        meta: {
            noHeader: true,
        },
        props: (route) => ({
            type: route.params.type,
        })
    },
    {
        path: "/survey/:type/:id",
        name: "Survey",
        component: Survey,
        meta: {
            noHeader: true,
        },
        props: (route) => ({
            type: route.params.type,
            id: route.params.id,
        })
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: "/",
            name: "home",
            component: Landing,
        },
        ...authRoutes,
        ...userProfileRoutes,
        ...legalRoutes,
        ...adminRoutes,
        ...surveyRoutes,
        {
            path: "/:keyword",
            name: "basic-page",
            component: BasicPage,
            props: (route) => ({
                keyword: route.params.keyword,
            }),
        },
        {
            path: "/:basic/:specific",
            name: "specific-page",
            component: SpecificPage,
            props: (route) => ({
                basic: route.params.basic,
                specific: route.params.specific,
            }),
            key: (route) => `${route.params.basic}-${route.params.specific}-${Date.now()}`,
        },
        {
            path: "/:pathMatch(.*)*",
            name: "error",
            component: NotFound,
        },
        
    ],
    scrollBehavior(to, from, savedPosition) {
        return { top: 0 };
    },
});

let firstLoad = true;
router.beforeResolve(async (to, from, next) => {
    const authStore = useAuthStore();
    const helper = useAppHelpers();
    let performLogout = authStore.isLogoutAllowed;

    if(!performLogout && to.name === 'logout'){
        next({ name: "home" });
    }
    
    // get router history
    
    const fromMetaType = from.meta?.type;
    const toMetaType = to.meta?.type;
    
    if(!helper.arraysAreEqual(fromMetaType, toMetaType)){
        firstLoad = false;
        await authStore.FETCH_USER_DATA();
    }
    else if(firstLoad){
        firstLoad = false;
        await authStore.FETCH_USER_DATA(true);
    }
    
    if (to.meta && to.meta.type) {
        let canAccessRoute = await authStore.canAccessRoute(to);
        if (canAccessRoute) {
            next();
        } else {
            next({ name: "home" });
        }
    } else {
        next();
    }
});

export default router;
